import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';
import type { FirstMoveInDateCommand } from './room-first-move-in-date';

/**
 * Unit별 첫 입주 가능일 — 새 endpoint가 아니라 기존 Room PATCH에 실제 부모 Room 경로와 optional unit_id를 쓴다.
 * 로컬 BE candidate(010, 미배포) 기준이며 기존 Room 변경 함수(changeRoomFirstMoveInDate)는 바꾸지 않는다.
 */
export interface UnitFirstMoveInDateReceipt {
  command_id: string;
  room_id: string;
  unit_id: string;
  revision: number;
  product: string;
  channel: string;
  first_move_in_date: string;
  provider_outcome: string | null;
}
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '슬롯의 첫 입주 가능일 변경 결과를 확인하지 못했습니다.',
  });
const receiptKeys = [
  'command_id',
  'room_id',
  'unit_id',
  'revision',
  'product',
  'channel',
  'first_move_in_date',
  'provider_outcome',
];
const isDate = (value: unknown): value is string =>
  typeof value === 'string' &&
  /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  Number.isFinite(Date.parse(`${value}T00:00:00Z`)) &&
  new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;

export async function changeUnitFirstMoveInDate(
  client: ServiceClient,
  roomId: string,
  unitId: string,
  command: FirstMoveInDateCommand,
): Promise<UnitFirstMoveInDateReceipt> {
  if (
    !isUuid(roomId) ||
    !isUuid(unitId) ||
    !isUuid(command.command_id) ||
    !Number.isSafeInteger(command.expected_sale_revision) ||
    command.expected_sale_revision < 0 ||
    !isDate(command.first_move_in_date) ||
    typeof command.reason !== 'string' ||
    !command.reason.trim() ||
    Array.from(command.reason).length > 500 ||
    Object.keys(command).length !== 4
  )
    throw invalid();
  const result = await client.patch<unknown>(`admin/rooms/${roomId}/first-move-in-date`, {
    json: { ...command, unit_id: unitId },
    expectedStatus: 200,
  });
  if (
    typeof result !== 'object' ||
    result === null ||
    Array.isArray(result) ||
    !receiptKeys.every((key) => Object.hasOwn(result, key)) ||
    Object.keys(result).filter((key) => key !== 'sale_scope').length !== receiptKeys.length ||
    ('sale_scope' in result && result.sale_scope !== 'UNIT')
  )
    throw invalid();
  const value = result as Record<string, unknown>;
  if (
    typeof value.command_id !== 'string' ||
    value.command_id.toLowerCase() !== command.command_id.toLowerCase() ||
    typeof value.room_id !== 'string' ||
    value.room_id.toLowerCase() !== roomId.toLowerCase() ||
    typeof value.unit_id !== 'string' ||
    value.unit_id.toLowerCase() !== unitId.toLowerCase() ||
    !Number.isSafeInteger(value.revision) ||
    (value.revision as number) <= command.expected_sale_revision ||
    typeof value.product !== 'string' ||
    typeof value.channel !== 'string' ||
    value.first_move_in_date !== command.first_move_in_date ||
    !(value.provider_outcome === null || typeof value.provider_outcome === 'string')
  )
    throw invalid();
  return value as unknown as UnitFirstMoveInDateReceipt;
}
